import { delay } from 'redux-saga';
import {
  all,
  call,
  put,
  cancel,
  take,
  fork,
  takeEvery,
  select,
} from 'redux-saga/effects';
import {
  fetchSubmission,
  deleteSubmission,
  fetchBridgedResource,
} from '@kineticdata/react';
import {
  addToast,
  addToastAlert,
  cloneSubmission,
  selectServicesKapp,
  C,
} from '@kineticdata/bundle-common';
import { parseActivityData } from '../../utils';
import { actions, types } from '../modules/submission';

const include =
  'details,values,form,form.attributes,form.kapp,form.kapp.attributes,form.kapp.space.attributes,' +
  'activities,activities.details,descendants,descendants.details,descendants.values,' +
  'parent,parent.descendants,parent.descendants.details,parent.descendants.values,' +
  'parent.activities,parent.activities.details,parent.form,parent.form.attributes';

export function* fetchSubmissionRequestSaga(action) {
  const { submission, error } = yield call(fetchSubmission, {
    id: action.payload,
    include,
  });

  if (error) {
    yield put(actions.fetchSubmissionFailure(error));
  } else {
    yield put(actions.fetchSubmissionSuccess(submission));
    // Fetch activity details if needed
    yield call(fetchSubmissionActivityDetails, submission);
  }
}

const activityDetailsFetchMapper = submission => activity => {
  const data = parseActivityData(activity);
  if (activity.type === 'ITSM Change' && !!data['Change Number']) {
    return {
      id: data['Change Number'],
      bridgedResourceName: 'Change Details',
    };
  } else if (activity.type === 'ITSM Incident' && !!data['Incident Number']) {
    return {
      id: data['Incident Number'],
      bridgedResourceName: 'Incident Details',
    };
  } else if (activity.type === 'ITSM Task' && !!data['Task ID']) {
    return {
      id: data['Task ID'],
      bridgedResourceName: 'Task Details',
    };
  } else if (activity.type.includes('Media Sanitation')) {
    return {
      id: activity.id,
      bridgedResourceName: 'Media Sanitation Details',
      values: {
        Id: submission.id,
        Query: activity.id,
      },
    };
  }
  return null;
};

function* fetchSubmissionActivityDetails(submission) {
  // Parse and filter activities to those that require additional details to be fetched
  const activities = submission.activities
    .map(activityDetailsFetchMapper(submission))
    .filter(Boolean);

  // Fetch data for each of these activities
  yield all(
    activities.map(payload =>
      put(actions.fetchActivityDetailsRequest(payload)),
    ),
  );
}

export function* fetchActivityDetailsSaga(action) {
  const { id, bridgedResourceName, values = {} } = action.payload;
  const kapp = yield select(selectServicesKapp);
  const { record, records } = yield call(fetchBridgedResource, {
    kappSlug: kapp && kapp.slug,
    formSlug: C.FORM_SLUG_SHARED_RESOURCES,
    bridgedResourceName,
    values: { Id: id, ...values },
  });

  yield put(
    // Data can be either a record or a list, depending on what type of activity
    // we're fetching. The Activity Node renderers know what to expect.
    actions.fetchActivityDetailsComplete({ id, data: record || records }),
  );
}

export function* fetchActivityLogsSaga(action) {
  const { id, type } = action.payload;
  const kapp = yield select(selectServicesKapp);
  const bridgedResourceName =
    type === 'Change'
      ? 'Change Work Info'
      : type === 'Incident'
        ? 'Incident Work Info'
        : type === 'Task'
          ? 'Task Work Info'
          : null;

  if (bridgedResourceName) {
    const { records, error } = yield call(fetchBridgedResource, {
      kappSlug: kapp && kapp.slug,
      formSlug: C.FORM_SLUG_SHARED_RESOURCES,
      bridgedResourceName,
      values: { Id: id },
    });

    yield put(
      actions.fetchActivityLogsComplete({ id, data: records || error }),
    );
  }
}

export function* cloneSubmissionRequestSaga(action) {
  const include = 'form,form.fields.details,form.pages';
  const { submission: origSubmission, error: origError } = yield call(
    fetchSubmission,
    {
      id: action.payload.id,
      include,
    },
  );

  if (origError) {
    addToastAlert({
      title: 'Failed to retrieve submission to duplicate',
      message: origError.message,
    });
  } else {
    // Find all fields that start with sa and clear out their values
    const saFields = origSubmission.form.fields
      .filter(
        field => field.name.match(/^sa-/i) || field.name === C.FIELD_IS_LEGACY,
      )
      .reduce(
        (o, f) => ({
          ...o,
          [f.name]: ['json', 'file'].includes(f.dataType) ? [] : null,
        }),
        {},
      );

    // Make the call to create the clone.
    const { submission, error } = yield call(cloneSubmission, {
      id: action.payload.id,
      values: {
        Status: 'Draft',
        // Override fields that should be cleared
        ...saFields,
        // If clone should be tracked, set value of sa-Submission Origin field to current submission's id
        [C.FIELD_SUBMISSION_ORIGIN]: !!action.payload.track
          ? action.payload.id
          : null,
      },
      currentPage: origSubmission.form.pages[0]
        ? { name: origSubmission.form.pages[0].name }
        : null,
      completed: false,
    });

    if (error) {
      addToastAlert({
        title: 'Failed to clone submission',
        message: error.message,
      });
    } else {
      addToast('Submission cloned successfully');

      if (typeof action.payload.success === 'function') {
        action.payload.success(submission);
      }
    }

    yield put(actions.setPendingEnd());
  }
}

export function* deleteSubmissionRequestSaga(action) {
  const { error } = yield call(deleteSubmission, {
    id: action.payload.id,
  });

  if (error) {
    addToastAlert({
      title: 'Failed to delete submission',
      message: error.message,
    });
  } else {
    addToast('Submission deleted successfully');
    if (typeof action.payload.success === 'function') {
      action.payload.success();
    }
  }
  yield put(actions.setPendingEnd());
}

export function* pollerTask(id) {
  let pollDelay = 10000;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    // Wait
    yield delay(pollDelay);
    // Query
    const { submission, error } = yield call(fetchSubmission, {
      id,
      include,
    });
    // If there is a server error dispatch the appropriate action and break out
    // of the while loop to stop polling.
    if (error) {
      addToast({ severity: 'danger', message: 'Failed to refresh submission' });
      yield put(actions.stopSubmissionPoller());
      break;
    } else {
      yield put(actions.fetchSubmissionSuccess(submission));
      pollDelay = Math.min(pollDelay + 10000, 60000);
      // Refetch activity details if needed
      yield call(fetchSubmissionActivityDetails, submission);
    }
  }
}

export function* watchSubmissionPoller() {
  let action;
  // eslint-disable-next-line no-cond-assign
  while ((action = yield take(types.START_SUBMISSION_POLLER))) {
    // start the poller in the background
    const poller = yield fork(pollerTask, action.payload);
    // wait for the message to stop the poller
    yield take(types.STOP_SUBMISSION_POLLER);
    // stop the poller by cancelling the background task
    yield cancel(poller);
  }
}

export function* watchSubmission() {
  yield takeEvery(types.FETCH_SUBMISSION_REQUEST, fetchSubmissionRequestSaga);
  yield takeEvery(types.CLONE_SUBMISSION_REQUEST, cloneSubmissionRequestSaga);
  yield takeEvery(types.DELETE_SUBMISSION_REQUEST, deleteSubmissionRequestSaga);
  yield takeEvery(
    types.FETCH_ACTIVITY_DETAILS_REQUEST,
    fetchActivityDetailsSaga,
  );
  yield takeEvery(types.FETCH_ACTIVITY_LOGS_REQUEST, fetchActivityLogsSaga);
}
